import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import usePageSEO from "@/hooks/usePageSEO";

const STATES = [
  { code: "KS", label: "Kansas" },
  { code: "CA", label: "California" },
  { code: "NY", label: "New York" },
  { code: "TX", label: "Texas" },
  { code: "FL", label: "Florida" },
  { code: "IL", label: "Illinois" },
  { code: "OTHER", label: "Other state" },
];

function Section({ id, title, children }) {
  return (
    <section
      id={id}
      data-testid={`compliance-section-${id}`}
      className="mb-12 scroll-mt-24"
    >
      <h2 className="font-serif-display text-2xl sm:text-3xl text-lw-navy leading-tight">
        {title}
      </h2>
      <div className="gold-hairline w-16 my-4 opacity-70" />
      <div className="text-sm sm:text-base text-lw-navy/85 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function Compliance() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("KS");

  usePageSEO({
    title: "Compliance & Disclosures — Lifespan Wealth Partners",
    description:
      "Producer capacity, license information, illustration disclosures, IUL tax treatment, Medicare TPMO, variable products, and state-specific disclosures.",
  });

  useEffect(() => {
    let cancelled = false;
    apiGet("/compliance/disclosures")
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const sections = data?.sections || [];
  const bySlug = (id) => sections.find((s) => s.id === id);

  const stateNote =
    bySlug("state-specific")?.state_notes?.[state] ||
    bySlug("state-specific")?.state_notes?.OTHER;

  return (
    <div data-testid="compliance-page" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Compliance & disclosures</span>
          <h1
            data-testid="compliance-headline"
            className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05]"
          >
            Important regulatory information.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            The disclosures below apply to all content on this site, including
            product pages, calculators, articles, and PDFs. Lifespan Wealth
            Partners is a licensed insurance producer, not a registered
            investment adviser.
          </p>

          {/* Section-by-section */}
          <div className="mt-12">
            {sections.map((s) => (
              <Section key={s.id} id={s.id} title={s.title}>
                {s.body && <p>{s.body}</p>}
                {s.table && (
                  <div
                    className="overflow-x-auto rounded-2xl border border-lw-navy/10 bg-white mt-4"
                    data-testid={`compliance-table-${s.id}`}
                  >
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-lw-ivory-warm text-left">
                          <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">
                            Product category
                          </th>
                          <th className="p-4 font-sans-ui text-xs uppercase tracking-wider text-lw-navy/60">
                            Primary regulator(s)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.map((row, i) => (
                          <tr key={i} className="border-t border-lw-navy/5">
                            <td className="p-4 font-semibold text-lw-navy">
                              {row.product}
                            </td>
                            <td className="p-4 text-lw-navy/80">
                              {row.regulator}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {s.state_notes && (
                  <div className="mt-4">
                    <label className="text-xs uppercase tracking-[0.14em] text-lw-navy/60 font-semibold">
                      Select your state
                    </label>
                    <select
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      data-testid="compliance-state-select"
                      className="mt-2 block rounded-xl border border-lw-navy/15 bg-white px-4 py-2.5 text-sm font-sans-ui outline-none focus:border-lw-gold"
                    >
                      {STATES.map((st) => (
                        <option key={st.code} value={st.code}>
                          {st.label}
                        </option>
                      ))}
                    </select>
                    <p
                      data-testid={`compliance-state-note-${state}`}
                      className="mt-4 p-4 rounded-2xl border border-lw-gold/30 bg-lw-ivory-warm text-lw-navy/85"
                    >
                      {stateNote}
                    </p>
                  </div>
                )}
              </Section>
            ))}
          </div>

          {/* Fallback while loading */}
          {sections.length === 0 && (
            <p className="mt-12 text-lw-muted" data-testid="compliance-loading">
              Loading disclosures…
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
